import { IconRocket } from '@tabler/icons-react';
import NSIcon from 'bricks/NSIcon/NSIcon';
import { useMemo, useState, useEffect } from 'react';
import { NSModal, NSRadioOption } from 'bricks';
import { IOptionAnyValue } from 'bricks/types';
import { AxiosError } from 'axios';
import { useCreateSubprojectFromProForma } from 'queries/ProForma/useCreateSubprojectFromProForma';
import useGetProForma from 'queries/ProForma/useGetProForma';
import { useUpdateProFormaSubprojectLink } from 'queries/ProForma/useUpdateProFormaSubprojectLink';
import useAuthContext from 'contexts/AuthContext';
import { useLoader } from 'contexts/LoaderContext';
import useToast from 'hooks/useToast';
import { useGetDevelopmentProjects } from 'hooks/useGetDevelopmentProjects';
import RenderIf from 'components/RenderIf/RenderIf';
import ProjectLinkSelect from 'components/LinkToProjectModal/ProjectLinkSelect/ProjectLinkSelect';
import { LINK_PROJECT_BUTTON_TEXT } from 'components/LinkToProjectModal/constants';
import { URLS } from 'services/urls';
import { PRO_FORMA_STATUS } from 'views/ProFormaHome/constants';
import { ProFormaModelIds } from 'constants/proForma';
import { formatProFormaAssetTypeName } from 'helpers/proForma';
import { isProFormaKeyStatsInProgress } from 'views/ProFormaTable/helpers';
import { useProFormaSocketContext } from '../../ProForma/socketContext/ProFormaSocketProvider';
import NewProjectForm, { SITE_OPTION } from './NewProjectForm';

const MODAL_MODE = {
    CREATE: 'create',
    LINK: 'link',
};

export interface ICreateProjectModalProps {
    isOpen: boolean;
    toggle: () => void;
}

const CreateProjectModal = ({ toggle, isOpen }: ICreateProjectModalProps) => {
    const { proForma, residentialLotMixes, updateProFormaLocal } = useProFormaSocketContext();
    const { selectedOrganizationId } = useAuthContext();
    const { showLoader, hideLoader } = useLoader();
    const { showSuccess, showError } = useToast();

    const [modalMode, setModalMode] = useState<typeof MODAL_MODE.CREATE | typeof MODAL_MODE.LINK>(MODAL_MODE.CREATE);
    const [selectedOption, setSelectedOption] = useState<string>(SITE_OPTION.NEW);
    const [newSiteName, setNewSiteName] = useState('');
    const [selectedSite, setSelectedSite] = useState<IOptionAnyValue | undefined>();
    const [selectedProject, setSelectedProject] = useState<IOptionAnyValue | undefined>();
    const [projectName, setProjectName] = useState<string>('');

    const isLinkMode = modalMode === MODAL_MODE.LINK;

    // Local flags instead of isPending: state flips synchronously on click, blocking double submits.
    const [isCreatingSubproject, setIsCreatingSubproject] = useState(false);
    const [isLinkingProject, setIsLinkingProject] = useState(false);

    const { mutate: createSubproject } = useCreateSubprojectFromProForma();
    const { mutate: updateSubprojectLink } = useUpdateProFormaSubprojectLink();

    // The socket payload may not carry the correlation ids the link picker needs; the REST detail does.
    const { data: proFormaDetail } = useGetProForma(proForma?.dealId ?? 0, proForma?.id ?? 0, {
        enabled: isOpen && Boolean(proForma?.dealId && proForma?.id)
            && (proForma?.assetTypeCorrelationId == null || proForma?.correlationId == null),
    });
    const assetTypeCorrelationId = proForma?.assetTypeCorrelationId ?? proFormaDetail?.assetTypeCorrelationId;
    const proFormaCorrelationId = proForma?.correlationId ?? proFormaDetail?.correlationId;

    const {
        data: existingDevelopmentProjects,
        isLoading: isLoadingDevelopmentProjects,
        refetch: refetchDevelopmentProjects,
    } = useGetDevelopmentProjects(selectedOrganizationId!, {
        enabled: Boolean(selectedOrganizationId) && isOpen,
    });

    useEffect(() => {
        if (isOpen && selectedOrganizationId) {
            refetchDevelopmentProjects();
        }
    }, [isOpen, selectedOrganizationId, refetchDevelopmentProjects]);

    useEffect(() => {
        if (!isOpen) {
            setModalMode(MODAL_MODE.CREATE);
            setSelectedProject(undefined);
        }
    }, [isOpen]);

    const devSiteOptions = existingDevelopmentProjects && existingDevelopmentProjects.length ? existingDevelopmentProjects.map(site => ({
        label: site.projectName,
        value: site.correlationId,
    })) : [];

    const isCreateDisabled = useMemo(() => {
        if (!projectName?.trim()) return true;

        if (existingDevelopmentProjects?.length && selectedOption === SITE_OPTION.EXISTING) {
            return !selectedSite;
        }

        return !newSiteName.trim();
    }, [newSiteName, selectedSite, projectName, existingDevelopmentProjects, selectedOption]);

    const addressFieldsSet = !!proForma?.address?.trim() && !!proForma?.city?.trim() && !!proForma?.state?.trim() && !!proForma?.zipCode?.trim();
    const isLandDevelopment = proForma?.proFormaModelId === ProFormaModelIds.LAND_DEVELOPMENT;
    const isCoordinatesMode = !!proForma?.useCoordinates;
    const isKeyStatsInProgress = isProFormaKeyStatsInProgress(proForma, residentialLotMixes);

    const isCreateProjectGated = isLandDevelopment
        || proForma?.proFormaStatusId !== PRO_FORMA_STATUS.SUBSTANTIALLY_COMPLETE
        || isCoordinatesMode
        || !addressFieldsSet
        || isKeyStatsInProgress;

    let createProjectGatedTooltip = 'Pro forma must be substantially complete and address fields set before creating a project';
    if (isLandDevelopment) {
        createProjectGatedTooltip = 'Creating a project from a Land Development pro forma is not yet supported';
    } else if (isCoordinatesMode) {
        createProjectGatedTooltip = 'Switch Basic Details off coordinates and enter an address to create a project';
    }

    const handleCreateProject = () => {
        if (isCreatingSubproject) return;
        if (!selectedOrganizationId || !proForma?.id || !proForma.dealId) return;

        setIsCreatingSubproject(true);
        showLoader({ message: 'Creating your project: ', highlightedText: projectName.trim() });

        createSubproject({
            organizationId: selectedOrganizationId,
            proFormaId: proForma.id,
            dealId: proForma.dealId,
            data: {
                saleDate: proForma.saleDate,
                projectName: projectName.trim(),
                developmentSiteName: selectedOption === SITE_OPTION.NEW ? newSiteName.trim() : undefined,
                existingDevelopmentSiteUuid: selectedOption === SITE_OPTION.EXISTING ? selectedSite?.value : undefined,
            },
        }, {
            onSuccess: data => {
                setTimeout(() => {
                    hideLoader();
                    toggle();
                    updateProFormaLocal({
                        subprojectId: data?.correlationId ?? proForma?.subprojectId ?? null,
                    });
                    window.open(`${URLS.PROJECTS_APP.PROJECTS}/project_overview/${data?.id}`, '_blank');
                    showSuccess('Project created successfully');
                    setIsCreatingSubproject(false);
                }, 2000);
            },
            onError: (error: Error) => {
                hideLoader();
                setIsCreatingSubproject(false);
                const isPermissionError = error instanceof AxiosError && error.response?.status === 403;
                const permissionErrorMessage = "You do not have permission to create a project from a pro forma. Please contact your organization admin.";
                if (isPermissionError) showError(permissionErrorMessage);
                else showError('Failed to create project. Please try again.');

                console.error('Error creating project:', error);
            },
        });
    };

    const handleLinkProject = () => {
        if (isLinkingProject || !selectedProject) return;
        if (!proForma?.id || !proForma.dealId) return;

        setIsLinkingProject(true);
        updateSubprojectLink({
            dealId: proForma.dealId,
            proFormaId: proForma.id,
            subprojectUuid: selectedProject.value,
        }, {
            onSuccess: () => {
                updateProFormaLocal({ subprojectId: selectedProject.value });
                showSuccess('Project linked successfully');
                toggle();
            },
            onSettled: () => setIsLinkingProject(false),
        });
    };

    useEffect(() => {
        if (proForma?.name && !projectName) {
            setProjectName(proForma.name);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [proForma]);

    return (
        <NSModal
            toggle={toggle}
            isOpen={isOpen}
            modalHeaderText="Link or create project"
            modalHeaderClassName="bg-primary text-white"
            modalFooterButtonText={isLinkMode ? LINK_PROJECT_BUTTON_TEXT : 'Create project'}
            modalFooterButtonFunction={isLinkMode ? handleLinkProject : handleCreateProject}
            modalFooterIsButtonFunctionDisabled={isLinkMode
                ? (!selectedProject || isLinkingProject)
                : (isCreateDisabled || isCreateProjectGated)}
            modalFooterDisabledTooltip={!isLinkMode && isCreateProjectGated ? createProjectGatedTooltip : ''}
            modalFooterButtonIsLoading={isLinkMode ? isLinkingProject : isCreatingSubproject}
        >
            <RenderIf isTrue={!isLinkMode}>
                <div className="text-center mb-2">
                    <div
                        className="d-inline-flex align-items-center justify-content-center bg-primary rounded-circle"
                        style={{ width: '37px', height: '37px' }}
                    >
                        <NSIcon icon={IconRocket} className="text-white" size="lg" />
                    </div>
                    <div className="text-white font-18">
                        You're one step away from launching a new project!
                    </div>
                    <div className="font-12">
                        Confirm the development site and name your project.
                    </div>
                </div>
            </RenderIf>

            <div className="d-flex mb-2">
                <NSRadioOption
                    id="create-a-new-project"
                    name="project-modal-mode"
                    label="Create a new project"
                    checked={!isLinkMode}
                    onChange={() => setModalMode(MODAL_MODE.CREATE)}
                />
                <NSRadioOption
                    id="link-to-existing-project"
                    name="project-modal-mode"
                    label="Link to existing project"
                    checked={isLinkMode}
                    onChange={() => setModalMode(MODAL_MODE.LINK)}
                    containerClassName="ml-3"
                />
            </div>

            <RenderIf isTrue={isLinkMode}>
                <ProjectLinkSelect
                    assetTypeCorrelationId={assetTypeCorrelationId}
                    assetTypeName={formatProFormaAssetTypeName(proForma?.proFormaModelName)}
                    proFormaCorrelationId={proFormaCorrelationId}
                    selectedOption={selectedProject}
                    onSelectOption={setSelectedProject}
                />
            </RenderIf>

            <RenderIf isTrue={!isLinkMode}>
                <NewProjectForm
                    selectedOption={selectedOption}
                    onSelectOption={setSelectedOption}
                    newSiteName={newSiteName}
                    onNewSiteNameChange={setNewSiteName}
                    selectedSite={selectedSite}
                    onSelectSite={setSelectedSite}
                    projectName={projectName}
                    onProjectNameChange={setProjectName}
                    devSiteOptions={devSiteOptions}
                    hasExistingSites={Boolean(existingDevelopmentProjects && existingDevelopmentProjects.length > 0)}
                    isLoadingSites={isLoadingDevelopmentProjects}
                />
            </RenderIf>
        </NSModal>
    );
};

export default CreateProjectModal;
